// Portal v2 Slice A — Action Center API client.
//
//   GET  /portal/attention          — everything awaiting the client
//   POST /portal/proofs/:id/approve — approve a proof (starts production)
//   POST /portal/proofs/:id/reject  — request changes (reason required)
//
// The `outstanding` figure on invoices is the deposit-and-payments-netted
// amount from the shared money formula — display THIS, never balanceDue.
import { api } from "@/lib/api";

export interface AttentionProof {
  id: string;
  version: number;
  orderId: string;
  orderNumber: number;
  projectName: string;
  sentAt: string;
}

export interface AttentionInvoice {
  id: string;
  invoiceNumber: number;
  status: string;
  invoiceDate: string;
  orderId: string;
  projectName: string;
  total: string;
  outstanding: string;
  /** False when card payment is off for this invoice — show "View invoice",
   *  never a "pay" button that can't pay. */
  canPayOnline: boolean;
}

export interface PortalAttention {
  proofs: AttentionProof[];
  invoices: AttentionInvoice[];
}

export async function fetchPortalAttention(): Promise<PortalAttention> {
  return api<PortalAttention>("/portal/attention");
}

/** The approval terms — the same words the emailed proof link shows
 *  (apps/api/src/services/proof-terms.ts). */
export interface ProofTerms {
  version: string;
  note: string;
  agree: string;
  points: string[];
}

export async function fetchProofTerms(): Promise<ProofTerms> {
  return (await api<{ terms: ProofTerms }>("/proof-terms")).terms;
}

/** Approving needs the signed terms: ticked, full name typed, terms version. */
export async function approvePortalProof(
  proofId: string,
  sign: { signedName: string; termsVersion: string },
): Promise<{ proof: { id: string; status: string } }> {
  return api(`/portal/proofs/${proofId}/approve`, {
    method: "POST",
    body: JSON.stringify({ agreed: true, signed_name: sign.signedName, terms_version: sign.termsVersion }),
  });
}

export async function rejectPortalProof(
  proofId: string,
  reason: string,
): Promise<{ proof: { id: string; status: string } }> {
  return api(`/portal/proofs/${proofId}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}
